import {
  BaseField,
  buildForm,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  FieldComponentType,
  Form,
  SubmitButton,
  toast,
} from "@bleu.builders/ui";
import { serialize } from "object-to-formdata";
import React from "react";
import { useForm } from "react-hook-form";
import { FormEncType, useActionData, useSubmit } from "react-router-dom";

import { getObjectsDifference } from "#/lib/objectDifference";

export function SettingsCard({
  title,
  fields,
  defaultValues,
  description = "",
  footerContent = undefined,
  actionText = "Salvar",
  action = undefined,
  encType = "multipart/form-data" as FormEncType,
  _method = "patch",
  method = "post",
  useFormSubmit = false,
  formReturn = undefined,
  customComponents = {},
}: {
  title: string;
  description?: string;
  fields: BaseField[];
  defaultValues: object;
  footerContent?: React.ReactNode;
  actionText?: string;
  action?: { path: string };
  encType?: FormEncType;
  _method?: string;
  method?: "post" | "get";
  useFormSubmit?: boolean;
  formReturn?: ReturnType<typeof useForm>;
  customComponents?: { [key: string]: FieldComponentType };
}) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const actionData = useActionData() as {
    data: { errors: object; success: boolean };
  };

  const data = actionData?.data;

  const form =
    formReturn ||
    useForm({
      defaultValues,
      errors: data?.errors,
    });

  const submit = useSubmit();

  const handleSubmit = async () => {
    if (useFormSubmit) return;

    const isValid = await form.trigger();
    if (!isValid) return;

    // if the mode is "update", we only send the changed values
    const values =
      _method === "patch"
        ? getObjectsDifference(defaultValues, form.getValues())
        : form.getValues();

    if (Object.keys(values).length === 0) {
      toast({
        title: "No changes detected",
        description: "No changes were detected. Please try again.",
        variant: "destructive",
      });
      return;
    }

    const formData = serialize(values, { allowEmptyArrays: true });
    submit(formData, { method: "post", encType });
  };

  return (
    <Card className="max-w-full border dark:border-2 shadow-sm bg-background">
      <Form
        {...form}
        action={action.path}
        method={method as "post" | "get"}
        encType={
          encType as "application/x-www-form-urlencoded" | "multipart/form-data"
        }
        onSubmit={() => setIsSubmitting(true)}
      >
        <input name="_method" type="hidden" value={_method} />
        <CardHeader className="pt-6 px-6">
          <CardTitle className="mt-0 p-0 text-xl font-semibold text-foreground mb-2">
            {title}
          </CardTitle>
          <CardDescription className="py-0 pl-0 text-sm">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-4">
            {buildForm(fields, form, undefined, customComponents)}
          </div>
        </CardContent>
        <CardFooter className="flex justify-between border-t px-6 py-3 text-sm">
          {footerContent ? footerContent : <div />}
          <SubmitButton
            isSubmitting={isSubmitting}
            className="h-8"
            type={useFormSubmit ? "submit" : "button"}
            onClick={handleSubmit}
          >
            {actionText}
          </SubmitButton>
        </CardFooter>
      </Form>
    </Card>
  );
}
